import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  ConditionalOptionValues,
  ConditionalStepField,
  CreditApplicationFields,
} from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { DillCheckbox } from '../../../general/Dill';
import AppDatePicker from './AppDatePicker';
import RadioInputs from './RadioInput';
import StateDropDown from './StateDropDown';
import AddressAutocompleteSelect from './AddressAutocompleteSelect';
import CheckboxList from './CheckboxList';
import OptionsDropDown from './OptionsDropDown';
import DocumentField from './DocumentField';
import { UploadFileInputs } from './UploadFileInput';
import {
  resolveFieldOptions,
  resolveDropdownFieldOptions,
  resolveSalesRepFieldLabel,
  SupplierRefOption,
} from './conditionalOptionUtils';
import ConditionalOptionPreview, { ConditionalOptionPreviewRef } from './ConditionalOptionPreview';
import { useConditionalOption } from './useConditionalOption';

export interface InternalSignatureDocumentFillableFieldsRef {
  validate: () => boolean;
}

// Thin adapter around the pure-renderer ConditionalOptionPreview + its useConditionalOption hook —
// same ~15-line shape as DynamicStepForm.tsx's own (unexported) ConditionalOptionField, just keyed
// by plain field.id instead of a synthetic multi-step uniqueId, since an internal signature document is flat.
// Neither ConditionalOptionPreview nor the hook has any wizard/multi-step dependency, so this is a
// straight port, not a rebuild.
const ConditionalOptionField = forwardRef<
  ConditionalOptionPreviewRef,
  {
    creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
    name: string;
    isRequired?: boolean;
    isRow?: boolean;
    optionList?: string;
    errorText?: string;
    externalSelectedOption: string;
    externalFieldValues: ConditionalOptionValues[];
    onChangeData: (data: { value: string; fieldValue: ConditionalOptionValues[] }) => void;
    branches?: SupplierRefOption[];
    salesReps?: SupplierRefOption[];
  }
>(
  (
    {
      creditApplicationOptionData,
      name,
      isRequired,
      isRow,
      optionList,
      errorText,
      externalSelectedOption,
      externalFieldValues,
      onChangeData,
      branches,
      salesReps,
    },
    ref
  ) => {
    const {
      selectedOption,
      optionStates,
      errors,
      handleSelectOption,
      handleChangeValue,
      handleAddGroup,
      handleRemoveGroup,
      validate,
    } = useConditionalOption({
      creditApplicationOptionData,
      externalSelectedOption,
      externalFieldValues,
      onChangeData,
    });

    useImperativeHandle(ref, () => ({ validate }));

    return (
      <ConditionalOptionPreview
        creditApplicationOptionData={creditApplicationOptionData}
        name={name}
        isRequired={isRequired}
        isRow={isRow}
        errorText={errorText}
        selectedOption={selectedOption}
        currentOptionState={optionStates[selectedOption] ?? null}
        fieldErrors={errors}
        optionList={optionList}
        onSelectOption={handleSelectOption}
        onChangeValue={handleChangeValue}
        onAddGroup={handleAddGroup}
        onRemoveGroup={handleRemoveGroup}
        branches={branches}
        salesReps={salesReps}
      />
    );
  }
);

// TEXT fields with a validationType — same formats, messages and as-you-type formatting as
// DynamicStepForm.tsx's credit application fields.
const digitsOf = (raw: string, max: number) => raw.replace(/\D/g, '').slice(0, max);
const FORMATTED_TEXT_RULES: Record<
  'EIN' | 'SSN' | 'PHONE',
  {
    format: (raw: string) => string;
    pattern: RegExp;
    placeholder: string;
    maxLength: number;
    hint: string;
    error: string;
  }
> = {
  EIN: {
    format: (raw) => {
      const digits = digitsOf(raw, 9);
      return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    },
    pattern: /^\d{2}-\d{7}$/,
    placeholder: 'XX-XXXXXXX',
    maxLength: 10,
    hint: 'Format: XX-XXXXXXX (e.g. 12-3456789)',
    error: 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
  },
  SSN: {
    format: (raw) => {
      const digits = digitsOf(raw, 9);
      if (digits.length > 5)
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    },
    pattern: /^\d{3}-\d{2}-\d{4}$/,
    placeholder: 'XXX-XX-XXXX',
    maxLength: 11,
    hint: 'Format: XXX-XX-XXXX (e.g. 123-45-6789)',
    error: 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
  },
  PHONE: {
    format: (raw) => {
      const digits = digitsOf(raw, 10);
      if (digits.length > 6)
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    },
    pattern: /^\d{3}-\d{3}-\d{4}$/,
    placeholder: 'XXX-XXX-XXXX',
    maxLength: 12,
    hint: 'Format: XXX-XXX-XXXX (e.g. 555-123-4567)',
    error: 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
  },
};

const getFormattedTextRule = (field: CreditApplicationFields) =>
  field.fromField === 'TEXT' && field.validationType && field.validationType !== 'NONE'
    ? FORMATTED_TEXT_RULES[field.validationType]
    : undefined;

// The format error for a filled-in EIN/SSN/Phone field; empty values are left to the required check.
export const getFormattedTextError = (field: CreditApplicationFields, value: unknown) => {
  const rule = getFormattedTextRule(field);
  const text = typeof value === 'string' ? value.trim() : '';
  return rule && text && !rule.pattern.test(text) ? rule.error : '';
};

const gridColsClassForCount = (count: number) =>
  count >= 3
    ? 'grid-cols-1 md:grid-cols-3'
    : count === 2
    ? 'grid-cols-1 md:grid-cols-2'
    : 'grid-cols-1';

// Controlled, flat field-type-switch renderer for an internal signature document's own fillable fields (its
// `fieldGroups`, flattened via `flattenInternalSignatureDocumentFields` and filtered to exclude
// INTERNAL_SIGNER slots — those are signature placeholders, not values). Reuses the exact same
// leaf components DynamicForm.tsx uses per fromField type, but keyed directly by `field.id`
// rather than DynamicForm's synthetic multi-step uniqueId, since an internal signature document is a flat
// single-page list, not a wizard.
//
// Extracted out of CreditApplicationTemplate/InternalSignatureDocumentsSection.tsx's `PreviewInternalSignatureDocumentFields`
// (which now wraps this with its own local preview state) so the exact same rendering/behavior is
// shared with the real fill-and-sign UI on SignInternalSignatureDocument.tsx.
//
// Exposes `validate()` via ref so a caller with a CONDITIONAL_SELECTED_FIELD present can fold its
// (ref-based, per DynamicStepForm.tsx's own pattern) validation into an overall "can I sign now?"
// check — every other field type here is validated the simpler way, by the caller just checking
// `values[field.id]` isn't empty (see SignInternalSignatureDocument.tsx's isFieldValueMissing).
export const InternalSignatureDocumentFillableFields = forwardRef<
  InternalSignatureDocumentFillableFieldsRef,
  {
    fields: CreditApplicationFields[];
    values: Record<string, any>;
    onChange: (fieldId: string, value: any, fieldValue?: ConditionalOptionValues[]) => void;
    errors?: Record<string, string>;
    fieldValues?: Record<string, ConditionalOptionValues[]>;
    groupSizes?: number[];
    branches?: SupplierRefOption[];
    salesReps?: SupplierRefOption[];
    // Live P21 Class 1 codes for the "Marketing Segment" field (id: marketing-segment) — see
    // resolveFieldOptions/resolveDropdownFieldOptions, and SignInternalSignatureDocument.tsx where
    // this is fetched.
    class1Options?: string[];
    // False drops the box around the fields — for pages that already group them in a card.
    bordered?: boolean;
    // Address search on "<prefix>-address" fields that fills the matching -city/-state/-zip
    // fields, like the credit application. Off unless the page opts in (internal forms do).
    addressAutocomplete?: boolean;
    // CURRENCY fields show commas and their decimal places (e.g. 12,500.00) once you leave the
    // field. Off unless the page opts in (internal forms do).
    currencyFormatting?: boolean;
  }
>(
  (
    {
      fields,
      values,
      onChange,
      errors,
      fieldValues,
      groupSizes,
      branches,
      salesReps,
      class1Options,
      bordered = true,
      addressAutocomplete = false,
      currencyFormatting = false,
    },
    ref
  ) => {
    // What's typed into a currency field while it has focus; formatted again on blur.
    const [rawCurrencyText, setRawCurrencyText] = useState<Record<string, string>>({});
    const boxClass = bordered ? 'rounded-md GREY_300-BORDER border p-3' : '';
    const conditionalRefs = useRef<Record<string, ConditionalOptionPreviewRef | null>>({});
    // EIN/SSN/Phone format errors, shown once the value is full length or the field loses focus.
    const [formatErrors, setFormatErrors] = useState<Record<string, string>>({});

    useImperativeHandle(ref, () => ({
      validate: () => {
        const nextFormatErrors = fields.reduce<Record<string, string>>((acc, field) => {
          const error = getFormattedTextError(field, values[field.id]);
          if (error) acc[field.id] = error;
          return acc;
        }, {});
        setFormatErrors(nextFormatErrors);
        const conditionalValid = Object.values(conditionalRefs.current).every(
          (r) => r?.validate?.() !== false
        );
        return conditionalValid && Object.keys(nextFormatErrors).length === 0;
      },
    }));

    if (fields.length === 0) return null;

    const renderField = (field: CreditApplicationFields) => {
      const value = values[field.id];
      const errorText = errors?.[field.id];
      let fieldComponent: React.ReactNode = null;
      let fullWidth = false;
      const isAddressSearch =
        addressAutocomplete &&
        field.fromField === 'TEXT' &&
        (field.id ?? '').endsWith('-address') &&
        !(field.id ?? '').includes('email');
      const showsSubtextBelow =
        isAddressSearch ||
        ['STATE_DROPDOWN', 'DROPDOWN', 'CHECKBOX_LIST'].includes(field.fromField);

      switch (field.fromField) {
        case 'TEXT':
        case 'NUMBER':
        case 'CURRENCY':
        case 'TEXT_AREA': {
          const currencyDecimalPlaces: number = field.currencyDecimalPlaces ?? 2;
          const fieldId = field.id ?? '';
          if (
            addressAutocomplete &&
            field.fromField === 'TEXT' &&
            fieldId.endsWith('-address') &&
            !fieldId.includes('email')
          ) {
            const idPrefix = fieldId.slice(0, -'-address'.length);
            fieldComponent = (
              <AddressAutocompleteSelect
                label={field.name}
                isRequired={field.isRequired}
                value={typeof value === 'string' ? value : ''}
                errorText={errorText}
                dataPrivate={!!field.isHashed}
                onTextChange={(text) => onChange(field.id, text)}
                onSelectSuggestion={(suggestion) => {
                  onChange(
                    field.id,
                    [suggestion.streetLine, suggestion.secondary].filter(Boolean).join(' ')
                  );
                  // Sibling fields may sit in another section of the form; values are by id.
                  onChange(`${idPrefix}-city`, suggestion.city);
                  onChange(`${idPrefix}-state`, suggestion.state);
                  onChange(`${idPrefix}-zip`, suggestion.zipcode);
                }}
              />
            );
            break;
          }
          if (currencyFormatting && field.fromField === 'CURRENCY') {
            const formatted =
              typeof value === 'number'
                ? value.toLocaleString('en-US', {
                    minimumFractionDigits: currencyDecimalPlaces,
                    maximumFractionDigits: currencyDecimalPlaces,
                  })
                : '';
            fieldComponent = (
              <AppInputField
                labelSubtext={field.labelSubtext ?? ''}
                label={field.name}
                inputType="text"
                value={rawCurrencyText[field.id] ?? formatted}
                isRequired={field.isRequired}
                prefix={field.prefix}
                suffix={field.suffix}
                onTextChange={(text: string) => {
                  // Digits and one decimal point, capped at the field's decimal places, with
                  // commas added to the whole-number part as you type; padded out on blur.
                  const digitsOnly = text.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                  const [wholePart, decimalPart] = digitsOnly.split('.');
                  const whole = wholePart.replace(/^0+(?=\d)/, '');
                  const decimals =
                    decimalPart === undefined
                      ? undefined
                      : decimalPart.slice(0, currencyDecimalPlaces);
                  const cleaned =
                    decimals === undefined || currencyDecimalPlaces === 0
                      ? whole
                      : `${whole}.${decimals}`;
                  const withCommas =
                    (whole
                      ? Number(whole).toLocaleString('en-US')
                      : decimals !== undefined
                      ? '0'
                      : '') +
                    (decimals !== undefined && currencyDecimalPlaces > 0 ? `.${decimals}` : '');
                  setRawCurrencyText((prev) => ({ ...prev, [field.id]: withCommas }));
                  const parsed = parseFloat(cleaned);
                  onChange(
                    field.id,
                    cleaned === '' || isNaN(parsed)
                      ? null
                      : Number(parsed.toFixed(currencyDecimalPlaces))
                  );
                }}
                onBlur={() =>
                  setRawCurrencyText((prev) => {
                    const { [field.id]: removed, ...rest } = prev;
                    return rest;
                  })
                }
                labelTextClassName="whitespace-pre-wrap"
                errorText={errorText}
              />
            );
            break;
          }
          const formattedTextRule = getFormattedTextRule(field);
          if (formattedTextRule) {
            const formatError = formatErrors[field.id];
            fieldComponent = (
              <div className="flex flex-col">
                <AppInputField
                  labelSubtext={field.labelSubtext ?? ''}
                  label={field.name}
                  inputType="text"
                  value={typeof value === 'string' ? value : value ?? ''}
                  isRequired={field.isRequired}
                  prefix={field.prefix}
                  suffix={field.suffix}
                  placeholder={formattedTextRule.placeholder}
                  maxLength={formattedTextRule.maxLength}
                  onTextChange={(text: string) => {
                    const formatted = formattedTextRule.format(text);
                    // Checked as soon as it's full length; a shorter value waits for blur.
                    setFormatErrors((prev) => ({
                      ...prev,
                      [field.id]:
                        formatted.length === formattedTextRule.maxLength
                          ? getFormattedTextError(field, formatted)
                          : '',
                    }));
                    onChange(field.id, formatted);
                  }}
                  onBlur={() =>
                    setFormatErrors((prev) => ({
                      ...prev,
                      [field.id]: getFormattedTextError(field, value),
                    }))
                  }
                  dataPrivate={!!field.isHashed}
                  labelTextClassName="whitespace-pre-wrap"
                  errorText={errorText || formatError}
                />
                {!errorText && !formatError && (
                  <p className="text-xs mt-0.5 text-gray-400">{formattedTextRule.hint}</p>
                )}
              </div>
            );
            break;
          }
          fieldComponent = (
            <AppInputField
              labelSubtext={field.labelSubtext ?? ''}
              label={field.name}
              inputType={
                field.fromField === 'TEXT_AREA'
                  ? 'textarea'
                  : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                  ? 'number'
                  : 'text'
              }
              value={
                (field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') &&
                typeof value === 'number'
                  ? value
                  : value ?? ''
              }
              isRequired={field.isRequired}
              prefix={field.prefix}
              suffix={field.suffix}
              onTextChange={(text: string) => {
                if (field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') {
                  if (!text) {
                    onChange(field.id, null);
                    return;
                  }
                  const parsed = parseFloat(text);
                  onChange(
                    field.id,
                    isNaN(parsed)
                      ? null
                      : field.fromField === 'CURRENCY'
                      ? Number(parsed.toFixed(currencyDecimalPlaces))
                      : parsed
                  );
                  return;
                }
                onChange(field.id, text);
              }}
              labelTextClassName="whitespace-pre-wrap"
              errorText={errorText}
            />
          );
          break;
        }

        case 'DATE':
          fieldComponent = (
            <AppDatePicker
              labelSubtext={field.labelSubtext ?? ''}
              label={field.name}
              selectedDate={value ?? null}
              isRequired={field.isRequired}
              onDateSelected={(date) => onChange(field.id, date)}
              errorText={errorText}
            />
          );
          break;

        case 'CHECK_BOX':
          fieldComponent = (
            <div className="flex flex-col">
              <div className="my-1 flex items-center w-full justify-start">
                <DillCheckbox
                  isChecked={typeof value === 'boolean' ? value : false}
                  onChange={(checked: boolean) => onChange(field.id, checked)}
                />
                <div className="ml-2 text-sm">{field.name}</div>
              </div>
              {errorText && <p className="text-xs mt-0.5 text-red-600">{errorText}</p>}
            </div>
          );
          break;

        case 'OPTION_FIELD':
          fieldComponent = (
            <RadioInputs
              labelSubtext={field.labelSubtext ?? ''}
              label={resolveSalesRepFieldLabel(field, value, salesReps)}
              id={field.name || field.id}
              options={resolveFieldOptions(field, branches, salesReps, class1Options)}
              isRequired={field.isRequired}
              selectedOption={typeof value === 'string' ? value : ''}
              className={field.isRow ? 'gap-2 flex items-center' : ''}
              onChange={(option: { value: string }) => onChange(field.id, option.value)}
              labelTextClassName="whitespace-pre-wrap"
              errorText={errorText}
            />
          );
          break;

        case 'STATE_DROPDOWN':
          fieldComponent = (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={typeof value === 'string' ? value : ''}
              errorText={errorText}
              onTextChange={(text) => onChange(field.id, text)}
            />
          );
          break;

        case 'CHECKBOX_LIST':
          fieldComponent = (
            <CheckboxList
              isRequired={field.isRequired}
              label={resolveSalesRepFieldLabel(field, value, salesReps)}
              options={resolveFieldOptions(field, branches, salesReps, class1Options)}
              value={typeof value === 'string' ? value : ''}
              errorText={errorText}
              onTextChange={(text) => onChange(field.id, text)}
            />
          );
          break;

        case 'DROPDOWN':
          fieldComponent = (
            <OptionsDropDown
              isRequired={field.isRequired}
              label={resolveSalesRepFieldLabel(field, value, salesReps)}
              options={resolveDropdownFieldOptions(field, branches, salesReps, class1Options)}
              value={typeof value === 'string' ? value : ''}
              errorText={errorText}
              onTextChange={(text) => onChange(field.id, text)}
            />
          );
          break;

        // FILE and DOCUMENT_FIELD are used by internal forms (FillInternalForm.tsx). A FILE value
        // is a browser File until the caller uploads it on submit.
        case 'FILE':
          fullWidth = true;
          fieldComponent = (
            <UploadFileInputs
              isRequired={field.isRequired}
              errorText={errorText}
              label={field.name}
              labelSubtext={field.labelSubtext ?? ''}
              selectedFile={value instanceof File ? value : null}
              selectedFileName={value && typeof value === 'object' ? value.name ?? null : null}
              onSelectFile={(data) => onChange(field.id, data.file)}
              onRemoveFile={() => onChange(field.id, null)}
            />
          );
          break;

        case 'DOCUMENT_FIELD':
          fullWidth = true;
          fieldComponent = (
            // Same styling as DynamicStepForm: the template's own weight/size/alignment, so section
            // headings ("Ship-To", "Bill-To") and HTML notes look like they do on the credit form.
            <div
              className={`w-full GREY_900-CLR ${field.fontWeight ?? ''} ${
                field.alignment === 'RIGHT'
                  ? 'text-right'
                  : field.alignment === 'CENTER'
                  ? 'text-center'
                  : 'text-left'
              }`}
              style={{ fontSize: `${field.fontSize ?? 14}px` }}>
              <DocumentField text={field.documentText || ''} className="w-full" />
            </div>
          );
          break;

        case 'CONDITIONAL_SELECTED_FIELD':
          fullWidth = true;
          fieldComponent = (
            <ConditionalOptionField
              ref={(el) => {
                conditionalRefs.current[field.id] = el;
              }}
              creditApplicationOptionData={field.conditionalOptionFields || null}
              name={field.name}
              isRequired={field.isRequired}
              isRow={field.isRow}
              optionList={field.optionList}
              errorText={errorText}
              externalSelectedOption={typeof value === 'string' ? value : ''}
              externalFieldValues={fieldValues?.[field.id] ?? []}
              onChangeData={(data) => onChange(field.id, data.value, data.fieldValue ?? [])}
              branches={branches}
              salesReps={salesReps}
            />
          );
          break;

        default:
          fieldComponent = (
            <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
          );
      }

      return (
        <div
          key={field.id}
          className={
            groupSizes && groupSizes.length > 0
              ? fullWidth
                ? 'w-full col-span-full'
                : 'w-full'
              : fullWidth
              ? 'w-full'
              : 'flex-shrink-0 flex-grow basis-[280px] sm:basis-[45%] lg:basis-[30%] max-w-full'
          }>
          <div className="py-2 w-full">
            {fieldComponent}
            {/* These components have no sub-label of their own; the others take labelSubtext. */}
            {field.labelSubtext && showsSubtextBelow && (
              <p className="text-2xs mt-0.5 text-left GREY_500-CLR whitespace-pre-wrap">
                {field.labelSubtext}
              </p>
            )}
          </div>
        </div>
      );
    };

    if (groupSizes && groupSizes.length > 0) {
      const rows: CreditApplicationFields[][] = [];
      let cursor = 0;
      for (const size of groupSizes) {
        rows.push(fields.slice(cursor, cursor + size));
        cursor += size;
      }

      return (
        // No gap between rows: each field's own py-2 already puts 16px between them.
        <div className={`${boxClass} flex flex-col`}>
          {rows
            .filter((row) => row.length > 0)
            .map((row, index) => (
              <div
                key={index}
                className={`grid ${gridColsClassForCount(row.length)} gap-2 md:gap-2`}>
                {row.map(renderField)}
              </div>
            ))}
        </div>
      );
    }

    return (
      <div className={`${boxClass} flex flex-wrap gap-4 md:gap-2`}>{fields.map(renderField)}</div>
    );
  }
);

export default InternalSignatureDocumentFillableFields;
